import React, { useState, useMemo } from "react";
import { updateMatchResult } from "services/matchService";
import * as Sentry from "@sentry/react";
import ScoreEditModal from "./scoreEditModal";

const roundOrder = {
  round_of_64: 1,
  round_of_32: 2,
  round_of_16: 3,
  round_of_8: 4,
  quarterfinal: 5,
  semifinal: 6,
  third_place: 7,
  final: 8,
};

const compareRounds = (roundA, roundB) => {
  const orderA = roundOrder[roundA] ?? 99;
  const orderB = roundOrder[roundB] ?? 99;
  return orderA - orderB;
};

const EliminationMatchesTable = ({ matches, setMatches, tournament, reloadMatches }) => {
  const [editingScores, setEditingScores] = useState({});
  const [editingMatchId, setEditingMatchId] = useState(null);
  const [searchTerm, setSearchTerm] = useState(""); // Single search for all matches
  const [resultFilter, setResultFilter] = useState("all"); // Single filter for all matches
  const [divisionFilter, setDivisionFilter] = useState("all"); // Filter by division/pool

  const eliminationMatches = useMemo(
    () =>
      matches
        .filter((m) => m.phase === "elimination")
        .sort((a, b) => compareRounds(a.round, b.round)),
    [matches]
  );

  const divisions = useMemo(() => {
    return Array.from(new Set(eliminationMatches.map((m) => m.division)));
  }, [eliminationMatches]);

  const hasCrosspoolMatches = useMemo(() =>
    matches?.some((m) => m.phase === "crosspool"), [matches]
  );

  const normalizeText = (text) =>
    text
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .toLowerCase();

  const getTeamDisplayName = (match, teamKey) => {
    if (match[teamKey]?.players) {
      return match[teamKey].players.map((p) => p.name).join(" & ");
    }
    return match[`${teamKey}Name`] || "BYE";
  };

  const handleScoreChange = (matchId, teamKey, value) => {
    setEditingScores((prev) => {
      const current = prev[matchId] || {
        team1: matches.find((m) => m.id === matchId)?.result?.team1 ?? 0,
        team2: matches.find((m) => m.id === matchId)?.result?.team2 ?? 0,
      };
      return {
        ...prev,
        [matchId]: {
          ...current,
          [teamKey]: Math.max(0, parseInt(value, 10) || 0),
        },
      };
    });
  };

  const handleScoreSubmit = async (matchId) => {
    const fallbackMatch = matches.find((m) => m.id === matchId);
    const result = {
      team1:
        editingScores[matchId]?.team1 ?? fallbackMatch?.result?.team1 ?? "",
      team2:
        editingScores[matchId]?.team2 ?? fallbackMatch?.result?.team2 ?? "",
    };

    if (!isNaN(result.team1) && !isNaN(result.team2)) {
      try {
        const newResult =
          result.team1 === 0 && result.team2 === 0 ? null : result;
        await updateMatchResult(matchId, newResult);

        setMatches((prev) =>
          prev.map((m) => (m.id === matchId ? { ...m, result: newResult } : m))
        );

        setEditingMatchId(null);
        setEditingScores((prev) => {
          const updated = { ...prev };
          delete updated[matchId];
          return updated;
        });
      reloadMatches();
      } catch (error) {
        Sentry.captureException(error);
        console.error("Erreur lors de la mise à jour du score :", error);
        alert("❌ Impossible d'enregistrer le score.");
      }
    } else {
      alert("⚠️ Veuillez saisir un score valide pour les deux équipes.");
    }
  };

  if (!eliminationMatches.length) {
    return <p>Aucun match éliminatoire.</p>;
  }

  const search = normalizeText(searchTerm);

  // Filter matches based on search, result filter, and division filter
  const filteredMatches = eliminationMatches.filter((match) => {
    const names = [
      normalizeText(getTeamDisplayName(match, "team1")),
      normalizeText(getTeamDisplayName(match, "team2")),
    ];
    const matchesSearch = !search || names.some((name) => name.includes(search));

    const hasResult = !!match.result;
    const matchesResultFilter =
      resultFilter === "all" ||
      (resultFilter === "with" && hasResult) ||
      (resultFilter === "without" && !hasResult);

    const matchesDivisionFilter =
      divisionFilter === "all" || match.division === divisionFilter;

    return matchesSearch && matchesResultFilter && matchesDivisionFilter;
  });

  const getDivisionLabel = (division) => {
    if (!tournament?.makeDivisions && hasCrosspoolMatches) {
      // Use pool terminology for crosspool tournaments
      if (division === "division1") return "Élimination Groupe 1";
      if (division === "division2") return "Élimination Groupe 2";
      if (division === "final") return "Finale Inter-Groupes";
    }
    // Use division terminology for regular tournaments
    if (division === "division1") return "Élimination Division 1";
    if (division === "division2") return "Élimination Division 2";
    if (division === "final") return "Finale Inter-Divisions";
    return "Éliminatoire principal";
  };

  return (
    <div className="w-full bg-slate-800 border border-slate-700 rounded-lg p-4 shadow text-slate-50">
      <h3 className="text-xl font-bold mb-4">Matchs Éliminatoires</h3>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <input
          type="text"
          placeholder="Rechercher équipe..."
          className="border rounded px-2 py-1 flex-1 text-lg"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          className="border rounded px-2 py-1"
          value={resultFilter}
          onChange={(e) => setResultFilter(e.target.value)}
        >
          <option value="all">Tous les résultats</option>
          <option value="with">Avec score</option>
          <option value="without">Sans score</option>
        </select>
        {divisions.length > 1 && (
          <select
            className="border rounded px-2 py-1"
            value={divisionFilter}
            onChange={(e) => setDivisionFilter(e.target.value)}
          >
            <option value="all">
              {!tournament?.makeDivisions && hasCrosspoolMatches ? "Tous les groupes" : "Toutes les divisions"}
            </option>
            {divisions
              .sort((a, b) => {
                if (a === "division1") return -1;
                if (b === "division1") return 1;
                if (a === "division2") return -1;
                if (b === "division2") return 1;
                return a.localeCompare(b);
              })
              .map((division) => (
                <option key={division} value={division}>
                  {getDivisionLabel(division)}
                </option>
              ))}
          </select>
        )}
      </div>

      {/* Single unified table */}
      <div className="overflow-x-auto">
        <table className="min-w-full table-auto text-xs md:text-sm">
          <thead className="bg-slate-800 text-slate-50">
            <tr>
              <th className="px-2 py-2">#</th>
              <th className="px-2 py-2">Home</th>
              <th className="px-2 py-2">Guest</th>
              <th className="px-2 py-2">Résultat</th>
              <th className="px-2 py-2">Round</th>
              {divisions.length > 1 && (
                <th className="px-2 py-2">
                  {!tournament?.makeDivisions && hasCrosspoolMatches ? "Groupe" : "Division"}
                </th>
              )}
              <th className="px-2 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredMatches.map((match, idx) => {
              const team1Name = getTeamDisplayName(match, "team1");
              const team2Name = getTeamDisplayName(match, "team2");

              const isEditable =
                team1Name !== "BYE" &&
                team2Name !== "BYE" &&
                team1Name !== "?" &&
                team2Name !== "?";

              return (
                <tr key={match.id} className="border-b">
                  <td className="px-2 py-1 text-center">{idx + 1}</td>
                  <td className="px-2 py-1">{team1Name}</td>
                  <td className="px-2 py-1">{team2Name}</td>
                  <td className="px-2 py-1 text-center">
                    {match.result
                      ? `${match.result.team1} - ${match.result.team2}`
                      : "—"}
                  </td>
                  <td className="px-2 py-1 text-center font-bold">
                    {match.round}
                  </td>
                  {divisions.length > 1 && (
                    <td className="px-2 py-1 text-center">
                      {getDivisionLabel(match.division)}
                    </td>
                  )}
                  <td className="px-2 py-1 text-center">
                    <button
                      onClick={() => setEditingMatchId(match.id)}
                      disabled={!isEditable || tournament.compiled}
                      className={`${
                        (isEditable && !tournament.compiled)
                          ? "bg-yellow-500 hover:bg-yellow-600"
                          : "bg-slate-700 cursor-not-allowed"
                      } text-white text-sm px-2 py-1 rounded`}
                    >
                      Modifier
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filteredMatches.length === 0 && (
        <p className="text-center text-slate-400 italic py-4">
          Aucun match trouvé.
        </p>
      )}

      {/* Modal */}
      {editingMatchId && (
        <ScoreEditModal
          matchId={editingMatchId}
          score={
            editingScores[editingMatchId] || {
              team1:
                matches.find((m) => m.id === editingMatchId)?.result?.team1 ??
                "",
              team2:
                matches.find((m) => m.id === editingMatchId)?.result?.team2 ??
                "",
            }
          }
          onChange={handleScoreChange}
          onCancel={() => setEditingMatchId(null)}
          onSave={handleScoreSubmit}
          team1Name={getTeamDisplayName(
            matches.find((m) => m.id === editingMatchId),
            "team1"
          )}
          team2Name={getTeamDisplayName(
            matches.find((m) => m.id === editingMatchId),
            "team2"
          )}
          goalsToWin={matches.find((m) => m.id === editingMatchId)?.goalsToWin || 1}
        />
      )}
    </div>
  );
};

export default EliminationMatchesTable;
